function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { cloneElement } from 'react';
import { node, number, object, oneOf, oneOfType, string } from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import { findChildByType, filterOutChildrenByType } from '../internals/utils/reactHelpers';
import SovosDialogTitle from '../sovos-dialog-title';
const SMALL = 'small';
const MEDIUM = 'medium';
const LARGE = 'large';
const getWidthStyle = (width, spacing) => {
  if (width === SMALL) {
    return {
      maxWidth: spacing(50)
    };
  }
  if (width === MEDIUM) {
    return {
      minWidth: spacing(75),
      maxWidth: spacing(87.5)
    };
  }
  if (width === LARGE) {
    return {
      minWidth: spacing(75),
      maxWidth: '80vw',
      width: '80vw'
    };
  }
  return {
    width
  };
};

/**
 * Dialogs can use the `SovosDialogTitle`, `SovosDialogContents`, and
 * `SovosDialogActions` components for styling and positioning their contents.
 */
const SovosDialog = _ref => {
  let {
    children,
    className,
    PaperProps = {},
    width = MEDIUM,
    ...rest
  } = _ref;
  const {
    spacing
  } = useTheme();
  const title = findChildByType(children, SovosDialogTitle);
  const renderTitle = () => {
    var _title$props$onClose, _title$props;
    const handleClose = (_title$props$onClose = title === null || title === void 0 || (_title$props = title.props) === null || _title$props === void 0 ? void 0 : _title$props.onClose) !== null && _title$props$onClose !== void 0 ? _title$props$onClose : rest.onClose;
    return title ? /*#__PURE__*/cloneElement(title, {
      onClose: handleClose !== null && handleClose !== void 0 ? handleClose : null
    }) : null;
  };
  return /*#__PURE__*/React.createElement(Dialog, _extends({
    className: clsx('sovosDialog', className),
    PaperProps: {
      style: {
        ...PaperProps.style
      },
      sx: {
        borderRadius: 'shape.borderRadius',
        padding: spacing(3),
        ...getWidthStyle(width, spacing)
      },
      ...PaperProps
    }
  }, rest), renderTitle(), filterOutChildrenByType(children, SovosDialogTitle));
};
SovosDialog.propTypes = {
  /**
   * The content of the dialog. Should be wrapped with `SovosDialogTitle`,
   * `SovosDialogContent`, and `SovosDialogActions`
   */
  children: node.isRequired,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * Props passed the MuiDialog paper component
   */
  PaperProps: object,
  /**
   * Width, can be 'small', 'medium', 'large' or an arbitrary number.
   *
   * - `small`: maxWidth: '400px'
   * - `medium`: minWidth: '600px', maxWidth: '700px'
   * - `large`: width: '80%', minWidth: '600px',
   */
  width: oneOfType([oneOf([SMALL, MEDIUM, LARGE]), number])
};
SovosDialog.baseComponent = {
  name: 'Dialog',
  link: 'dialog/'
};
export default SovosDialog;